/**
 * useOpenalexRetrievalJob
 *
 * Shared hook for the OpenAlex retrieval modal:
 *   - Preview count (fast) + sample streaming
 *   - KB loading and selection
 *   - Job launch + post-launch navigation
 *
 * Simpler than useOpsRetrievalJob: a single retrieval mode (metadata + abstract),
 * no Google-Patents test or OPS-fallback dialog.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  openalexRetrievalService,
  type OpenAlexQuery,
  type OpenAlexPreviewResult,
  type OpenAlexPreviewSample,
} from '../services/openalexRetrievalService';
import { knowledgeBaseService, type KnowledgeBase } from '../services/knowledgeBaseService';

export interface UseOpenalexRetrievalJobReturn {
  previewResult: OpenAlexPreviewResult | null;
  previewing: boolean;
  previewError: string | null;
  samplesLoading: boolean;
  samplesError: string | null;

  kbs: KnowledgeBase[];
  kbsLoading: boolean;
  selectedKbId: string;
  setSelectedKbId: (id: string) => void;
  newKbName: string;
  setNewKbName: (name: string) => void;
  kbReady: boolean;

  launching: boolean;
  launchError: string | null;

  startPreview: (query: OpenAlexQuery) => Promise<void>;
  handleLaunch: (query: OpenAlexQuery, limit?: number | null) => Promise<void>;
  resetPreview: () => void;
}

export function useOpenalexRetrievalJob(subProjectId: string): UseOpenalexRetrievalJobReturn {
  const navigate = useNavigate();

  const [previewResult, setPreviewResult] = useState<OpenAlexPreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [samplesError, setSamplesError] = useState<string | null>(null);
  const samplesAbortRef = useRef<AbortController | null>(null);

  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [kbsLoading, setKbsLoading] = useState(false);
  const [selectedKbId, setSelectedKbId] = useState('');
  const [newKbName, setNewKbName] = useState('');

  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  const kbReady =
    selectedKbId === '__new__' ? newKbName.trim().length > 0 : selectedKbId.length > 0;

  useEffect(() => {
    let cancelled = false;
    setKbsLoading(true);
    knowledgeBaseService
      .getAll()
      .then((all) => {
        if (cancelled) return;
        const spKbs = all.filter((kb) => kb.sub_project_id === subProjectId);
        setKbs(spKbs);
        if (spKbs.length > 0) setSelectedKbId(spKbs[0].id);
      })
      .catch(() => { if (!cancelled) setKbs([]); })
      .finally(() => { if (!cancelled) setKbsLoading(false); });
    return () => { cancelled = true; };
  }, [subProjectId]);

  useEffect(() => {
    return () => { samplesAbortRef.current?.abort(); };
  }, []);

  const startPreview = useCallback(async (query: OpenAlexQuery) => {
    samplesAbortRef.current?.abort();
    setPreviewing(true);
    setPreviewError(null);
    setSamplesError(null);
    setSamplesLoading(false);

    try {
      const countResult = await openalexRetrievalService.previewCount(query);
      setPreviewResult({ ...countResult, sample: [] });
      setPreviewing(false);

      if (countResult.total_count > 0) {
        setSamplesLoading(true);
        samplesAbortRef.current = openalexRetrievalService.streamSamples(
          countResult.filter,
          countResult.total_count,
          (sample: OpenAlexPreviewSample) =>
            setPreviewResult((prev) => (prev ? { ...prev, sample: [...prev.sample, sample] } : prev)),
          () => setSamplesLoading(false),
          (message) => { setSamplesError(message); setSamplesLoading(false); },
        );
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setPreviewError(e?.response?.data?.detail ?? e?.message ?? 'Preview failed');
      setPreviewing(false);
    }
  }, []);

  const resetPreview = useCallback(() => {
    samplesAbortRef.current?.abort();
    setPreviewResult(null);
    setPreviewError(null);
    setSamplesLoading(false);
    setSamplesError(null);
    setLaunchError(null);
  }, []);

  const handleLaunch = useCallback(
    async (query: OpenAlexQuery, limit?: number | null) => {
      if (!previewResult || !kbReady) return;
      setLaunching(true);
      setLaunchError(null);
      try {
        const params: Parameters<typeof openalexRetrievalService.startJob>[0] = {
          sub_project_id: subProjectId,
          query,
          filter: previewResult.filter,
          total_count: previewResult.effective_count,
        };
        if (limit != null && limit > 0) params.max_works = limit;
        if (selectedKbId === '__new__') {
          params.new_kb_name = newKbName.trim();
        } else {
          params.target_kb_id = selectedKbId;
        }
        const job = await openalexRetrievalService.startJob(params);
        navigate(`/openalex-retrieval-progress/${job.id}`);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { detail?: string } }; message?: string };
        setLaunchError(e?.response?.data?.detail ?? e?.message ?? 'Failed to start job');
        setLaunching(false);
      }
    },
    [previewResult, kbReady, subProjectId, selectedKbId, newKbName, navigate],
  );

  return {
    previewResult,
    previewing,
    previewError,
    samplesLoading,
    samplesError,
    kbs,
    kbsLoading,
    selectedKbId,
    setSelectedKbId,
    newKbName,
    setNewKbName,
    kbReady,
    launching,
    launchError,
    startPreview,
    handleLaunch,
    resetPreview,
  };
}
